import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges
} from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { combineLatest, of } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { FollowUpTableDialog } from 'entity/dialogs/follow-up-table/follow-up-table.dialog';

import {
  EntityConnectionCountMapViewFragment, GetEntityById2, GetEntityById2GQL, GetEntityConnectionsGQL
} from 'generated/graphql';

import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { CampaignContextState } from 'app/_state/campaign-context/campaign-context-state';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { extractConnectionCountByEntityTypeId } from 'entity/helpers/connections-count-helpers';
import { isNilOrEmpty } from 'helpers';
import { FeatureFlagService } from 'feature-flags/feature-flag.service';

@Component({
  selector: 'app-entity-tabbed-card',
  templateUrl: './entity-tabbed-card.component.html',
  styleUrls: ['./entity-tabbed-card.component.scss']
})
export class EntityTabbedCardComponent extends WithDisposableSubject(WithChangesSubject(class {})) implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() isCollapsable: boolean;
  @Input() isCollapsed: boolean;
  @Input() activeTab: 'profile' | 'connections';
  @Output() activeTabChange = new EventEmitter<'profile' | 'connections'>();
  @Output() isCollapsedChange = new EventEmitter<boolean>();

  get selectedTabIndex() {
    if (!this.activeTab) {
      return 0;
    }
    return ['profile', 'connections'].indexOf(this.activeTab);
  }
  entity: GetEntityById2.GetEntity;
  defaultConnectionCount: EntityConnectionCountMapViewFragment;

  connectedEntities = [];

  constructor(
    private _dialog: MatDialog,
    private _apollo: Apollo,
    private _getEntityGQL: GetEntityById2GQL,
    private _getEntityConnectionsGQL: GetEntityConnectionsGQL,
    private _cd: ChangeDetectorRef,
    private _store: Store<CampaignContextState>,
    private _actions$: Actions,
    public _featureService: FeatureFlagService
  ) {
    super();
  }

  ngOnInit(): void {
    const $entityData = this._initWatchKeys().pipe(
      switchMap(({ campaignId, entityId, defaultEntityTypeId }) => this._getEntityGQL.watch({
        entityId, campaignId,
        withToplines: true,
        withBestAddress: true,
        withBestEmail: true,
        withBestPhone: true,
        withConnectionCounts: true
      }, {
        fetchPolicy: 'cache-first'
      }).valueChanges.pipe(
        takeUntil(this.disposer),
        map(({ data }) => data?.getEntity),
        map((entity) => {
          const defaultConnectionCount = extractConnectionCountByEntityTypeId(
            defaultEntityTypeId, entity
          );
          return { entity, defaultConnectionCount };
        })
      ))
    );

    $entityData.pipe(
      switchMap(({ entity, defaultConnectionCount }) => {
        if (isNilOrEmpty(defaultConnectionCount)) {
          return of([]);
        }
        return this._getEntityConnectionsGQL.watch({
          entityId: entity.id,
          campaignId: entity.campaignContextId,
          entityConnectionTypeId: defaultConnectionCount.entityConnectionType.id,
          first: 100
        }).valueChanges.pipe(
          takeUntil(this.disposer),
          filter(r => !!r.data),
          map(({ data }) => data.getEntityConnections.nodes.map(
            (entityConnection) => [
              entityConnection.entity1,
              entityConnection.entity2
            ].find((et) => entity.id !== et.id)
          ))
        );
      }),
    ).subscribe((ces) => {
      this.connectedEntities = ces;
      this._cd.markForCheck();
    });

    $entityData.subscribe(({ entity, defaultConnectionCount }) => {
      this.entity = entity;
      this.defaultConnectionCount = defaultConnectionCount;
      this._cd.markForCheck();
    });
  }

  private _initWatchKeys() {
    const inputKeys$ = this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId && !!entityId)
    );

    const defaultEntityTypeId$ = this._store.select(selectCurrentCampaign).pipe(
      takeUntil(this.disposer),
      map(c => c?.defaultEntityType?.id)
    );

    return combineLatest([inputKeys$, defaultEntityTypeId$]).pipe(
      map(([inputKeys, defaultEntityTypeId]) => ({
        campaignId: inputKeys.campaignId,
        entityId: inputKeys.entityId,
        defaultEntityTypeId
      }))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  onToggleCollapsedClick(isCollapsed) {
    this.isCollapsed = isCollapsed;
    this.isCollapsedChange.emit(isCollapsed);
  }

  onSelectedTabChange(event: MatTabChangeEvent) {
    const tabNameFromIndex = ['profile', 'connections'][event.index];
    this.activeTabChange.emit(tabNameFromIndex as any);
  }

  showFollowUpDetailsClick(event: MouseEvent) {
    event.stopImmediatePropagation();
    FollowUpTableDialog.open(this._dialog, {
      data: { campaignId: this.campaignId, entityId: this.entityId }
    });
  }
}
